import React, { useCallback, useMemo, useState, Suspense, lazy } from 'react';
import { motion } from 'framer-motion';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { PlayIcon, RefreshCwIcon, TrendingUpIcon, ActivityIcon, TargetIcon, Zap, BoxIcon, BarChart3Icon } from 'lucide-react';
// Lazy load 3D visualization
const MonteCarloVisualization3D = lazy(() => import('./MonteCarloVisualization3D'));
interface MonteCarloSimulationProps {
  language?: 'en' | 'vi';
}
const MonteCarloSimulation: React.FC<MonteCarloSimulationProps> = ({
  language = 'en'
}) => {
  const [isRunning, setIsRunning] = useState(false);
  const [simulations, setSimulations] = useState<number>(1000);
  const [timeHorizon, setTimeHorizon] = useState<number>(252);
  const [results, setResults] = useState<any>(null);
  const [chartData, setChartData] = useState<any[]>([]);
  const [visualizationMode, setVisualizationMode] = useState<'2d' | '3d'>('2d');
  // Portfolio parameters based on actual performance
  const portfolioParams = useMemo(() => ({
    meanReturn: 0.0017,
    volatility: 0.0117,
    sharpeRatio: 8.01,
    currentValue: 117.06
  }), []);
  const translations = useMemo(() => ({
    en: {
      title: 'Monte Carlo',
      titleHighlight: 'Simulation Model',
      subtitle: 'Statistical Projection Based on Limited Historical Data',
      runSimulation: 'Run Simulation',
      running: 'Running...',
      reset: 'Reset',
      numSimulations: 'Number of Simulations',
      timeHorizon: 'Time Horizon (Trading Days)',
      oneMonth: '1 Month',
      threeMonths: '3 Months',
      sixMonths: '6 Months',
      oneYear: '1 Year',
      results: 'Simulation Results',
      medianReturn: 'Median Outcome',
      percentile5: '5th Percentile',
      percentile95: '95th Percentile',
      probabilityProfit: 'Probability of Gain',
      expectedValue: 'Expected Value',
      lowerBound: '5th Percentile',
      upperBound: '95th Percentile',
      disclaimer: 'This simulation is a statistical model based on 58 days of historical data. It does not predict future performance and should not be used for investment decisions. Actual results may vary significantly.',
      parameters: 'Model Parameters (58-Day Sample)',
      dailyReturn: 'Mean Daily Return',
      dailyVolatility: 'Daily Volatility',
      modelNote: 'Note: Limited sample size may not capture full market conditions',
      view2D: '2D Chart',
      view3D: '3D Visualization',
      loading3D: 'Loading 3D visualization...'
    },
    vi: {
      title: 'Mô Hình Mô Phỏng',
      titleHighlight: 'Monte Carlo',
      subtitle: 'Dự Báo Thống Kê Dựa Trên Dữ Liệu Lịch Sử Hạn Chế',
      runSimulation: 'Chạy Mô Phỏng',
      running: 'Đang Chạy...',
      reset: 'Đặt Lại',
      numSimulations: 'Số Lượng Mô Phỏng',
      timeHorizon: 'Thời Gian (Ngày Giao Dịch)',
      oneMonth: '1 Tháng',
      threeMonths: '3 Tháng',
      sixMonths: '6 Tháng',
      oneYear: '1 Năm',
      results: 'Kết Quả Mô Phỏng',
      medianReturn: 'Kết Quả Trung Vị',
      percentile5: 'Phân Vị 5',
      percentile95: 'Phân Vị 95',
      probabilityProfit: 'Xác Suất Tăng',
      expectedValue: 'Giá Trị Kỳ Vọng',
      lowerBound: 'Phân Vị 5',
      upperBound: 'Phân Vị 95',
      disclaimer: 'Đây là mô hình thống kê dựa trên 58 ngày dữ liệu lịch sử. Nó không dự đoán hiệu suất tương lai và không nên dùng cho quyết định đầu tư. Kết quả thực tế có thể khác biệt đáng kể.',
      parameters: 'Tham Số Mô Hình (Mẫu 58 Ngày)',
      dailyReturn: 'Lợi Nhuận Hàng Ngày Trung Bình',
      dailyVolatility: 'Biến Động Hàng Ngày',
      modelNote: 'Lưu ý: Kích thước mẫu hạn chế có thể không phản ánh đầy đủ điều kiện thị trường',
      view2D: 'Biểu Đồ 2D',
      view3D: 'Trực Quan 3D',
      loading3D: 'Đang tải trực quan 3D...'
    }
  }), []);
  const t = translations[language];
  // Optimized random normal distribution
  const randomNormal = useCallback((mean: number, stdDev: number): number => {
    const u1 = Math.random();
    const u2 = Math.random();
    const z0 = Math.sqrt(-2.0 * Math.log(u1)) * Math.cos(2.0 * Math.PI * u2);
    return z0 * stdDev + mean;
  }, []);
  // Optimized Monte Carlo simulation
  const runMonteCarloSimulation = useCallback(() => {
    setIsRunning(true);
    setTimeout(() => {
      const paths: number[][] = [];
      const finalValues: number[] = [];
      const batchSize = 100;
      const numBatches = Math.ceil(simulations / batchSize);
      for (let batch = 0; batch < numBatches; batch++) {
        const batchEnd = Math.min((batch + 1) * batchSize, simulations);
        for (let sim = batch * batchSize; sim < batchEnd; sim++) {
          const path = [portfolioParams.currentValue];
          let currentValue = portfolioParams.currentValue;
          for (let day = 1; day <= timeHorizon; day++) {
            const dailyReturn = randomNormal(portfolioParams.meanReturn, portfolioParams.volatility);
            currentValue = currentValue * (1 + dailyReturn);
            path.push(currentValue);
          }
          paths.push(path);
          finalValues.push(currentValue);
        }
      }
      finalValues.sort((a, b) => a - b);
      const median = finalValues[Math.floor(finalValues.length / 2)];
      const percentile5 = finalValues[Math.floor(finalValues.length * 0.05)];
      const percentile95 = finalValues[Math.floor(finalValues.length * 0.95)];
      const mean = finalValues.reduce((sum, val) => sum + val, 0) / finalValues.length;
      const profitCount = finalValues.filter(val => val > portfolioParams.currentValue).length;
      const probabilityProfit = profitCount / finalValues.length * 100;
      const sampleRate = Math.max(1, Math.floor(timeHorizon / 100));
      const chartData = [];
      for (let day = 0; day <= timeHorizon; day += sampleRate) {
        const dayValues = paths.map(path => path[day]).sort((a, b) => a - b);
        chartData.push({
          day,
          median: dayValues[Math.floor(dayValues.length / 2)],
          p5: dayValues[Math.floor(dayValues.length * 0.05)],
          p95: dayValues[Math.floor(dayValues.length * 0.95)]
        });
      }
      setResults({
        median,
        percentile5,
        percentile95,
        mean,
        probabilityProfit,
        medianReturn: (median - portfolioParams.currentValue) / portfolioParams.currentValue * 100,
        p5Return: (percentile5 - portfolioParams.currentValue) / portfolioParams.currentValue * 100,
        p95Return: (percentile95 - portfolioParams.currentValue) / portfolioParams.currentValue * 100
      });
      setChartData(chartData);
      setIsRunning(false);
    }, 100);
  }, [simulations, timeHorizon, portfolioParams, randomNormal]);
  const resetSimulation = useCallback(() => {
    setResults(null);
    setChartData([]);
  }, []);
  const CustomTooltip = useCallback(({
    active,
    payload,
    label
  }: any) => {
    if (active && payload && payload.length) {
      return <div className="bg-slate-900/95 backdrop-blur-sm border border-white/10 rounded-lg p-3 shadow-xl" data-id="element-905">
          <p className="text-white text-sm font-medium mb-2" data-id="element-906">Day {label}</p>
          {payload.map((entry: any, index: number) => <p key={index} className="text-sm" style={{
          color: entry.color
        }} data-id="element-907">
              {entry.name}: ${entry.value.toFixed(2)}
            </p>)}
        </div>;
    }
    return null;
  }, []);
  const stats = useMemo(() => [{
    icon: TargetIcon,
    label: t.medianReturn,
    value: results?.medianReturn,
    subValue: results?.median,
    color: 'text-purple-500'
  }, {
    icon: TrendingUpIcon,
    label: t.upperBound,
    value: results?.p95Return,
    subValue: results?.percentile95,
    color: 'text-green-500'
  }, {
    icon: TrendingUpIcon,
    label: t.lowerBound,
    value: results?.p5Return,
    subValue: results?.percentile5,
    color: 'text-orange-500'
  }, {
    icon: Zap,
    label: t.probabilityProfit,
    value: results?.probabilityProfit,
    subValue: `${simulations} simulations`,
    color: 'text-cyan-500'
  }], [results, t, simulations]);
  return <section className="w-full py-12 md:py-24 bg-gradient-to-br from-secondary via-background to-secondary relative overflow-hidden" data-id="element-908">
      <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-909">
        <div className="absolute top-1/4 -left-48 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" data-id="element-910" />
        <div className="absolute bottom-1/4 -right-48 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" data-id="element-911" />
      </div>

      <div className="container mx-auto px-4 relative z-10" data-id="element-912">
        <div className="text-center mb-8 md:mb-16" data-id="element-913">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-5xl font-display font-bold mb-4" data-id="element-914">
            {t.title} <span className="gradient-text" data-id="element-915">{t.titleHighlight}</span>
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-lg md:text-xl text-muted max-w-2xl mx-auto" data-id="element-916">
            {t.subtitle}
          </motion.p>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-secondary/30 rounded-2xl p-4 md:p-8 border border-white/10 mb-8 md:mb-12" data-id="element-917">
          <div className="grid md:grid-cols-2 gap-6 md:gap-8 mb-6" data-id="element-918">
            <div data-id="element-919">
              <label className="block text-sm font-medium mb-3" data-id="element-920">
                {t.numSimulations}: {simulations}
              </label>
              <input type="range" min="100" max="5000" step="100" value={simulations} onChange={e => setSimulations(Number(e.target.value))} className="w-full h-2 bg-background rounded-lg appearance-none cursor-pointer accent-blue-600" disabled={isRunning} data-id="element-921" />
              <div className="flex justify-between text-xs text-muted mt-1" data-id="element-922">
                <span data-id="element-923">100</span>
                <span data-id="element-924">5,000</span>
              </div>
            </div>

            <div data-id="element-925">
              <label className="block text-sm font-medium mb-3" data-id="element-926">
                {t.timeHorizon}
              </label>
              <div className="grid grid-cols-2 gap-2" data-id="element-927">
                {[{
                days: 21,
                label: t.oneMonth
              }, {
                days: 63,
                label: t.threeMonths
              }, {
                days: 126,
                label: t.sixMonths
              }, {
                days: 252,
                label: t.oneYear
              }].map(({
                days,
                label
              }) => <button key={days} onClick={() => setTimeHorizon(days)} className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${timeHorizon === days ? 'bg-blue-600 text-white' : 'bg-background/50 text-muted hover:bg-background'}`} disabled={isRunning} data-id="element-928">
                    {label}
                  </button>)}
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-929">
            <button onClick={runMonteCarloSimulation} disabled={isRunning} className="px-6 md:px-8 py-3 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-930">
              {isRunning ? <>
                  <RefreshCwIcon size={20} className="animate-spin" data-id="element-931" />
                  {t.running}
                </> : <>
                  <PlayIcon size={20} data-id="element-932" />
                  {t.runSimulation}
                </>}
            </button>

            {results && <button onClick={resetSimulation} className="px-6 md:px-8 py-3 bg-background/50 border border-white/10 text-primary rounded-xl font-semibold hover:bg-background transition-all flex items-center justify-center gap-2" data-id="element-933">
                <RefreshCwIcon size={20} data-id="element-934" />
                {t.reset}
              </button>}
          </div>
        </motion.div>

        {results && <>
            <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="bg-secondary/30 rounded-2xl p-4 md:p-8 border border-white/10 mb-8 md:mb-12" data-id="element-935">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 gap-4" data-id="element-936">
                <h3 className="text-xl md:text-2xl font-bold flex items-center gap-2" data-id="element-937">
                  <ActivityIcon className="text-purple-500" size={24} data-id="element-938" />
                  {t.results}
                </h3>

                <div className="flex gap-2" data-id="element-939">
                  <button onClick={() => setVisualizationMode('2d')} className={`px-4 py-2 rounded-lg font-medium transition-all text-sm flex items-center gap-2 ${visualizationMode === '2d' ? 'bg-blue-600 text-white shadow-lg' : 'bg-background/50 text-muted hover:bg-background'}`} data-id="element-940">
                    <BarChart3Icon size={18} data-id="element-941" />
                    {t.view2D}
                  </button>
                  <button onClick={() => setVisualizationMode('3d')} className={`px-4 py-2 rounded-lg font-medium transition-all text-sm flex items-center gap-2 ${visualizationMode === '3d' ? 'bg-purple-600 text-white shadow-lg' : 'bg-background/50 text-muted hover:bg-background'}`} data-id="element-942">
                    <BoxIcon size={18} data-id="element-943" />
                    {t.view3D}
                  </button>
                </div>
              </div>

              {visualizationMode === '2d' ? <div className="w-full h-[300px] md:h-[400px] mb-6" data-id="element-944">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-945">
                    <AreaChart data={chartData} margin={{
                top: 5,
                right: 10,
                left: 0,
                bottom: 5
              }} data-id="element-946">
                      <defs data-id="element-947">
                        <linearGradient id="rangeGradient" x1="0" y1="0" x2="0" y2="1" data-id="element-948">
                          <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} data-id="element-949" />
                          <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} data-id="element-950" />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" data-id="element-951" />
                      <XAxis dataKey="day" stroke="#94a3b8" tick={{
                  fill: '#94a3b8',
                  fontSize: 10
                }} data-id="element-952" />
                      <YAxis stroke="#94a3b8" tick={{
                  fill: '#94a3b8',
                  fontSize: 10
                }} data-id="element-953" />
                      <Tooltip content={<CustomTooltip data-id="element-955" />} data-id="element-954" />
                      <Area type="monotone" dataKey="p95" stroke="none" fill="url(#rangeGradient)" isAnimationActive={false} data-id="element-956" />
                      <Area type="monotone" dataKey="p5" stroke="none" fill="#1e293b" isAnimationActive={false} data-id="element-957" />
                      <Line type="monotone" dataKey="median" stroke="#8b5cf6" strokeWidth={2} dot={false} isAnimationActive={false} data-id="element-958" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div> : <Suspense fallback={<div className="w-full h-[500px] flex items-center justify-center bg-slate-900/50 rounded-xl" data-id="element-960">
                      <div className="text-center" data-id="element-961">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4" data-id="element-962"></div>
                        <p className="text-muted" data-id="element-963">{t.loading3D}</p>
                      </div>
                    </div>} data-id="element-959">
                  <MonteCarloVisualization3D chartData={chartData} timeHorizon={timeHorizon} currentValue={portfolioParams.currentValue} data-id="element-964" />
                </Suspense>}

              <p className="text-xs md:text-sm text-muted text-center mt-4" data-id="element-965">
                {visualizationMode === '2d' ? '90% confidence interval (5th to 95th percentile range)' : 'Interactive 3D visualization - drag to rotate, scroll to zoom'}
              </p>
            </motion.div>

            <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8" data-id="element-966">
              {stats.map((stat, index) => {
            const Icon = stat.icon;
            return <div key={index} className="bg-secondary/50 backdrop-blur-sm rounded-2xl p-4 md:p-6 border border-white/10" data-id="element-967">
                    <div className="flex items-center gap-3 mb-3" data-id="element-968">
                      <Icon className={stat.color} size={20} data-id="element-969" />
                      <span className="text-xs md:text-sm text-muted" data-id="element-970">
                        {stat.label}
                      </span>
                    </div>
                    <div className="text-2xl md:text-3xl font-bold mb-1" data-id="element-971">
                      {stat.value?.toFixed(stat.label.includes('Probability') ? 1 : 2)}
                      {stat.label.includes('Probability') ? '%' : stat.label.includes('Return') || stat.label.includes('Outcome') ? '%' : ''}
                    </div>
                    <div className="text-xs md:text-sm text-muted" data-id="element-972">
                      {typeof stat.subValue === 'number' ? `$${stat.subValue.toFixed(2)}` : stat.subValue}
                    </div>
                  </div>;
          })}
            </motion.div>
          </>}

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-secondary/30 rounded-2xl p-4 md:p-6 border border-white/10 mb-6" data-id="element-973">
          <h3 className="text-lg md:text-xl font-bold mb-4" data-id="element-974">{t.parameters}</h3>
          <div className="grid sm:grid-cols-3 gap-4 text-xs md:text-sm mb-4" data-id="element-975">
            <div data-id="element-976">
              <span className="text-muted" data-id="element-977">{t.dailyReturn}:</span>
              <span className="ml-2 font-medium" data-id="element-978">
                {(portfolioParams.meanReturn * 100).toFixed(3)}%
              </span>
            </div>
            <div data-id="element-979">
              <span className="text-muted" data-id="element-980">{t.dailyVolatility}:</span>
              <span className="ml-2 font-medium" data-id="element-981">
                {(portfolioParams.volatility * 100).toFixed(3)}%
              </span>
            </div>
            <div data-id="element-982">
              <span className="text-muted" data-id="element-983">Sharpe Ratio:</span>
              <span className="ml-2 font-medium" data-id="element-984">
                {portfolioParams.sharpeRatio.toFixed(2)}
              </span>
            </div>
          </div>
          <p className="text-xs text-muted italic" data-id="element-985">{t.modelNote}</p>
        </motion.div>

        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} className="text-center" data-id="element-986">
          <p className="text-xs md:text-sm text-muted max-w-3xl mx-auto font-medium" data-id="element-987">
            {t.disclaimer}
          </p>
        </motion.div>
      </div>
    </section>;
};
export default MonteCarloSimulation;